import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Box, Paper, SelectChangeEvent, Typography } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useGetWorkTypesQuery } from '../../../../store/api/timesheet/timesheetWorkTypesApi'
import { acceptApproval } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { hasChanges } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'
import BonusCodes from './BonusCodes/BonusCodes'
import Gratifications from './TravelingsheetPDF/gratifications/Gratifications'
import TimesheetServices from './TimesheetServices'
import { useIsTimesheetApproved } from '../../../../utils/hooks'
import { handleDetails, handleTimesheetApprovalDetails } from '../../../../store/slices/timesheet/timesheetPageSlice'
import WorkRegimeAndAgent from './WorkRegimeAndAgent'
import PresentationalNotesAndComments from '../../../PresentationalNotesAndComments'
import ImageDisplay from './ImageDisplay'
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../../../store/timesheetStore'
import { useGetTimesheetQuery } from '../../../../utils/hooks/useGetTimesheetQuery'

function TimeSheetPDF() {
    const dispatch = useDispatch<TimesheetStoreDispatch>()
    const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
    const timesheetApprovalDetailsAccept = useSelector(
        (state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
    )
    const { data: workTypes } = useGetWorkTypesQuery()
    const { data: timesheet } = useGetTimesheetQuery()
    const isTimeSheetApproved = useIsTimesheetApproved()

    const [workType, setWorkType] = useState(
        String(isTimeSheetApproved ? (timesheetPage?.approval?.workTypeId ?? 0) : timesheetPage.workTypeId),
    )

    const [comments, setComments] = useState<string>('')

    useEffect(() => {
        if (isTimeSheetApproved) {
            setComments(
                timesheetPage?.approval?.comments ??
                    (timesheetPage?.schedulerNotes ? `\n${timesheetPage.schedulerNotes}` : ''),
            )
        } else {
            setComments(timesheetPage?.schedulerNotes ?? '')
        }
    }, [isTimeSheetApproved, timesheetPage?.approval?.comments, timesheetPage?.schedulerNotes])

    const services = useMemo(() => timesheetPage?.services || [], [timesheetPage?.services])

    const timesheetComments = useMemo(() => timesheet?.comments || '', [timesheet?.comments])

    const handleWork = (event: SelectChangeEvent<string | string[]>) => {
        setWorkType(event.target.value as string)
        if (isTimeSheetApproved) {
            dispatch(
                acceptApproval({
                    ...timesheetApprovalDetailsAccept,
                    workTypeId: Number(event.target.value),
                }),
            )
        } else {
            dispatch(
                handleDetails({
                    workTypeId: Number(event.target.value),
                }),
            )
        }
        dispatch(hasChanges({ hasChangesFromReviewer: true }))
    }

    const handleExtraComments = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        setComments(newValue)
        dispatch(
            acceptApproval({
                comments: newValue,
            }),
        )
        if (!timesheetApprovalDetailsAccept?.hasChangesFromReviewer) {
            dispatch(hasChanges({ hasChangesFromReviewer: true }))
        }
    }

    useEffect(() => {
        if (timesheet?.approval) {
            dispatch(handleTimesheetApprovalDetails(timesheet.approval))
        }
    }, [timesheet?.approval, dispatch])

    return (
        <>
            <Paper
                id='pdf-timesheet'
                sx={{
                    padding: '1.5rem 2.5rem',
                    borderRadius: '10px',
                    boxShadow: 'none',
                }}
            >
                <Box
                    sx={{
                        margin: '2rem 0',
                    }}
                >
                    <Typography
                        variant='h5'
                        sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            fontSize: '1.5rem',
                            lineHeight: '133.4%',
                            color: 'var(--primary-dark)',
                        }}
                    >
                        Folha de Ponto
                    </Typography>
                </Box>
                <TimesheetServices services={services} />
                <WorkRegimeAndAgent
                    workTypes={workTypes}
                    isTimeSheetApproved={isTimeSheetApproved}
                    workType={workType}
                    handleWork={handleWork}
                />
                <BonusCodes />
                <Gratifications />
                <ImageDisplay />
                <PresentationalNotesAndComments
                    type='sub-section'
                    title='Notas Adicionais'
                    label='Notas da Folha de Ponto'
                    content={timesheetComments.trim()}
                />
            </Paper>

            <Paper
                id='pdf-timesheet-comments'
                sx={{
                    padding: '1.5rem 2.5rem',
                    borderRadius: '10px',
                    boxShadow: 'none',
                }}
            >
                <PresentationalNotesAndComments
                    type='section'
                    title='Comentários do Escalador à Folha de Ponto'
                    label='Comentários'
                    content={comments}
                    presentational={false}
                    onChangeHandler={handleExtraComments}
                />
            </Paper>
        </>
    )
}

export default TimeSheetPDF
